import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Eye, FileSpreadsheet, Library, Lock, Package, Plus, Save, Trash2, Unlink, Users, X } from "lucide-react";
import { useToast } from "@/shared/providers/ToastProvider";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { CataloguePicker, type CatalogueItem } from "../components/CataloguePicker";
import {
  attachAdminInsurerCompanies,
  attachAdminInsurerHospitals,
  createAdminInsurer,
  detachAdminInsurerCompany,
  detachAdminInsurerHospital,
  fetchAdminCatalogue,
  fetchAdminCompanies,
  fetchAdminInsurerNetwork,
  fetchAdminInsurers,
  setAdminCompanyPackages,
  updateAdminInsurer,
  type AdminInsurer,
  type Catalogue,
  type CatalogueCompany,
  type CatalogueHospital,
  type InsurerCompany,
  type InsurerDetails,
  type NetworkHospital,
} from "../api/insurerApi";
import { setWorkContext } from "../workContext";

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError || err instanceof Error) return err.message;
  return fallback;
}

const buttonClass =
  "inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60";
const ghostClass =
  "inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent/20 hover:border-accent disabled:opacity-60";
const rowAction = "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-primary hover:bg-accent/20";

type View = { mode: "list" } | { mode: "create" } | { mode: "detail"; id: number };
type Tab = "profile" | "companies" | "network";

/** Champs modifiables de la fiche (le nom et le code sont figés après l'enregistrement). */
type ProfileForm = { status: "active" | "inactive" } & Record<keyof InsurerDetails, string>;

const PROFILE_GROUPS: Array<{ title: string; fields: Array<{ key: keyof InsurerDetails; label: string; placeholder?: string; type?: string }> }> = [
  {
    title: "Contrat avec SafeCheck",
    fields: [
      { key: "contract_start", label: "Début du contrat", type: "date" },
      { key: "contract_end", label: "Fin du contrat", type: "date" },
    ],
  },
  {
    title: "Délai de traitement convenu",
    fields: [
      { key: "processing_days", label: "Délai total (jours)", type: "number" },
      { key: "share_admin", label: "Part Admin (%)", type: "number" },
      { key: "share_audit", label: "Part médecin (%)", type: "number" },
      { key: "share_claims", label: "Part Sinistre (%)", type: "number" },
    ],
  },
  {
    title: "Coordonnées",
    fields: [
      { key: "address", label: "Adresse", placeholder: "Avenue, numéro, commune" },
      { key: "city", label: "Ville", placeholder: "Kinshasa" },
      { key: "phone", label: "Téléphone", placeholder: "+243 …" },
      { key: "email", label: "Email", type: "email" },
      { key: "website", label: "Site web" },
    ],
  },
  {
    title: "Identifiants légaux",
    fields: [
      { key: "license_number", label: "N° d'agrément (ARCA)" },
      { key: "rccm", label: "RCCM" },
      { key: "id_nat", label: "ID. Nat." },
      { key: "nif", label: "N° impôt (NIF)" },
    ],
  },
  {
    title: "Interlocuteur santé",
    fields: [
      { key: "contact_name", label: "Nom" },
      { key: "contact_role", label: "Fonction", placeholder: "Responsable santé" },
      { key: "contact_phone", label: "Téléphone" },
      { key: "contact_email", label: "Email", type: "email" },
    ],
  },
];

function toProfileForm(insurer: AdminInsurer): ProfileForm {
  const form = { status: insurer.status } as ProfileForm;
  PROFILE_GROUPS.forEach((group) =>
    group.fields.forEach(({ key }) => (form[key] = String(insurer[key] ?? "").slice(0, key.startsWith("contract_") ? 10 : undefined))),
  );
  return form;
}

/** Jours par acteur et par étape : médecin = examen et transmission ; Sinistre = contrôle et validation 3/4 + note de paiement 1/4. */
function processingSplit(days: string, admin: string, audit: string, claims: string): string | null {
  const total = Number(days);
  const shares = [Number(admin), Number(audit), Number(claims)];
  if (!total || shares.some((value) => !value)) return null;
  if (shares[0] + shares[1] + shares[2] !== 100) return "La répartition doit totaliser 100 %.";
  const d = (share: number, part = 1) => (Math.round(total * share * part) / 100).toLocaleString("fr-FR");
  return `Admin ${d(shares[0])} j · médecin ${d(shares[1])} j (examen et transmission) · Sinistre ${d(shares[2])} j (contrôle et validation ${d(shares[2], 0.75)}, note de paiement ${d(shares[2], 0.25)})`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

function companyItem(company: CatalogueCompany): CatalogueItem {
  return {
    id: company.id,
    name: company.name,
    detail: [
      company.sector,
      `${company.agents_count ?? 0} agent(s)`,
      `${company.dependants_count ?? 0} ayant(s) droit`,
      company.insurer_name ? `actuellement chez ${company.insurer_name}` : "sans assureur",
    ]
      .filter(Boolean)
      .join(" · "),
    warning: company.insurer_name
      ? `Transfert : elle quittera ${company.insurer_name}, dont les factures passées restent chez ${company.insurer_name}.`
      : undefined,
  };
}

function hospitalItem(hospital: CatalogueHospital): CatalogueItem {
  return {
    id: hospital.id,
    name: hospital.name,
    detail: [hospital.type, hospital.city, hospital.insurers.length > 0 ? `réseau de ${hospital.insurers.join(", ")}` : "dans aucun réseau"]
      .filter(Boolean)
      .join(" · "),
  };
}

/** Entreprises sans assureur d'abord, puis celles à transférer ; les inactives ne peuvent pas être reprises. */
function availableCompanies(catalogue: Catalogue | null): CatalogueCompany[] {
  return [...(catalogue?.companies ?? [])]
    .filter((c) => !c.is_mine && c.status === "active")
    .sort((a, b) => Number(a.insurer_id !== null) - Number(b.insurer_id !== null) || a.name.localeCompare(b.name));
}

/** true/false : transfert à demander ou non ; null : l'utilisateur a annulé. */
function confirmTransfer(catalogue: Catalogue | null, ids: number[], target: string): boolean | null {
  const elsewhere = (catalogue?.companies ?? []).filter((c) => ids.includes(c.id) && c.insurer_id !== null && !c.is_mine);
  if (elsewhere.length === 0) return false;
  const list = elsewhere.map((c) => `• ${c.name} (${c.insurer_name})`).join("\n");
  const ok = window.confirm(
    `Ces entreprises sont assurées ailleurs :\n${list}\n\nLes transférer chez ${target} avec leurs agents ? Leurs factures passées restent chez l'ancien assureur.`,
  );
  return ok ? true : null;
}

/** Cases à cocher des packages de l'assureur (au moins un est obligatoire). */
function PackageChecklist({ packages, selected, onChange }: { packages: Catalogue["packages"]; selected: number[]; onChange: (ids: number[]) => void }) {
  if (packages.length === 0) {
    return <p className="text-sm text-amber-700">Cet assureur n'a encore aucun package actif : créez-les d'abord dans Packages.</p>;
  }
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2">
      {packages.map((pkg) => (
        <label key={pkg.id} className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={selected.includes(pkg.id)}
            onChange={(e) => onChange(e.target.checked ? [...selected, pkg.id] : selected.filter((id) => id !== pkg.id))}
          />
          {pkg.name} <span className="font-mono text-xs text-muted">{pkg.code}</span>
        </label>
      ))}
    </div>
  );
}

/**
 * Admin : assureurs. Par défaut la liste ; création (fiche, contrat avec SafeCheck, prestataires repris du catalogue) ;
 * puis pour chaque assureur sa fiche (nom et code figés), ses entreprises créées en amont avec leurs packages autorisés,
 * et son réseau de prestataires, chacun avec la grille tarifaire négociée par cet assureur (obligatoire).
 */
export function InsurersPage() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [insurers, setInsurers] = useState<AdminInsurer[]>([]);
  const [view, setView] = useState<View>({ mode: "list" });
  const [tab, setTab] = useState<Tab>("profile");
  const [companies, setCompanies] = useState<InsurerCompany[]>([]);
  const [network, setNetwork] = useState<NetworkHospital[]>([]);
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const NEW_INSURER = { name: "", code: "", contract_start: "", contract_end: "", processing_days: "", share_admin: "10", share_audit: "50", share_claims: "40" };
  const [newInsurer, setNewInsurer] = useState(NEW_INSURER);
  const [newHospitalIds, setNewHospitalIds] = useState<number[]>([]);
  const [profile, setProfile] = useState<ProfileForm | null>(null);
  const [pickCompanies, setPickCompanies] = useState<number[] | null>(null);
  const [pickPackages, setPickPackages] = useState<number[]>([]);
  const [editPackages, setEditPackages] = useState<{ companyId: number; ids: number[] } | null>(null);
  const [pickHospitals, setPickHospitals] = useState<number[]>([]);

  const selectedId = view.mode === "detail" ? view.id : null;
  const selected = insurers.find((insurer) => insurer.id === selectedId) ?? null;

  const loadInsurers = useCallback(async () => {
    try {
      setInsurers(await fetchAdminInsurers());
    } catch (err) {
      setError(errorMessage(err, "Chargement des assureurs impossible."));
    }
  }, []);

  const loadSelected = useCallback(async (insurerId: number | null) => {
    try {
      if (!insurerId) {
        setCatalogue(await fetchAdminCatalogue());
        return;
      }
      const [companyRows, networkRows, catalogueRows] = await Promise.all([
        fetchAdminCompanies(insurerId),
        fetchAdminInsurerNetwork(insurerId),
        fetchAdminCatalogue(insurerId),
      ]);
      setCompanies(companyRows);
      setNetwork(networkRows);
      setCatalogue(catalogueRows);
    } catch (err) {
      setError(errorMessage(err, "Chargement de l'assureur impossible."));
    }
  }, []);

  useEffect(() => {
    void loadInsurers();
  }, [loadInsurers]);

  useEffect(() => {
    if (view.mode === "list") return;
    void loadSelected(selectedId);
    setPickCompanies(null);
    setPickPackages([]);
    setEditPackages(null);
    setPickHospitals([]);
  }, [view.mode, selectedId, loadSelected]);

  useEffect(() => {
    setProfile(selected ? toProfileForm(selected) : null);
  }, [selected]);

  const filteredInsurers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? insurers.filter((insurer) => `${insurer.code} ${insurer.name}`.toLowerCase().includes(q)) : insurers;
  }, [insurers, search]);
  const otherCompanyItems = useMemo(() => availableCompanies(catalogue).map(companyItem), [catalogue]);
  const allHospitalItems = useMemo(() => (catalogue?.hospitals ?? []).map(hospitalItem), [catalogue]);
  const otherHospitalItems = useMemo(() => (catalogue?.hospitals ?? []).filter((h) => !h.in_network).map(hospitalItem), [catalogue]);
  const packages = catalogue?.packages ?? [];
  const withoutGrid = network.filter((hospital) => (hospital.grids ?? []).length === 0).length;

  const run = async (action: () => Promise<void>, success: string) => {
    setError(null);
    setSaving(true);
    try {
      await action();
      showToast(success);
    } catch (err) {
      setError(errorMessage(err, "Enregistrement impossible."));
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    await Promise.all([loadSelected(selectedId), loadInsurers()]);
  };

  const openDetail = (id: number, nextTab: Tab = "profile") => {
    setError(null);
    setTab(nextTab);
    setView({ mode: "detail", id });
  };

  const handleCreateInsurer = () =>
    run(async () => {
      const created = await createAdminInsurer({
        name: newInsurer.name.trim(),
        code: newInsurer.code.trim(),
        contract_start: newInsurer.contract_start || null,
        contract_end: newInsurer.contract_end || null,
        processing_days: Number(newInsurer.processing_days),
        share_admin: Number(newInsurer.share_admin),
        share_audit: Number(newInsurer.share_audit),
        share_claims: Number(newInsurer.share_claims),
        hospital_ids: newHospitalIds,
      });
      setNewInsurer(NEW_INSURER);
      setNewHospitalIds([]);
      await loadInsurers();
      openDetail(created.id, "network");
    }, "Assureur créé : ajoutez une grille à chaque prestataire, créez ses packages puis rattachez ses entreprises.");

  const handleSaveProfile = () =>
    run(async () => {
      if (!selected || !profile) return;
      const numberOrNull = (value: string) => (value.trim() ? Number(value) : null);
      await updateAdminInsurer(selected.id, {
        name: selected.name,
        code: selected.code,
        ...profile,
        processing_days: numberOrNull(profile.processing_days),
        share_admin: numberOrNull(profile.share_admin),
        share_audit: numberOrNull(profile.share_audit),
        share_claims: numberOrNull(profile.share_claims),
      });
      await loadInsurers();
    }, "Fiche assureur enregistrée.");

  const handleAttachCompanies = () => {
    if (!selected || !pickCompanies || pickCompanies.length === 0 || pickPackages.length === 0) return;
    const transfer = confirmTransfer(catalogue, pickCompanies, selected.name);
    if (transfer === null) return;
    return run(async () => {
      await attachAdminInsurerCompanies(selected.id, pickCompanies, pickPackages, transfer);
      setPickCompanies(null);
      setPickPackages([]);
      await refresh();
    }, "Entreprises rattachées avec leurs agents et leurs packages autorisés.");
  };

  const handleSavePackages = () =>
    run(async () => {
      if (!selected || !editPackages) return;
      await setAdminCompanyPackages(selected.id, editPackages.companyId, editPackages.ids);
      setEditPackages(null);
      await refresh();
    }, "Packages autorisés enregistrés.");

  const handleDetachCompany = (row: InsurerCompany) => {
    if (!selected) return;
    if (!window.confirm(`Retirer « ${row.name} » de ${selected.name} ? Elle reste dans le catalogue avec ses agents ; ses factures restent chez ${selected.name}.`)) return;
    return run(async () => {
      await detachAdminInsurerCompany(selected.id, row.id);
      await refresh();
    }, "Entreprise retirée : elle reste dans le catalogue.");
  };

  const handleAttachHospitals = () =>
    run(async () => {
      if (!selectedId || pickHospitals.length === 0) return;
      await attachAdminInsurerHospitals(selectedId, pickHospitals);
      setPickHospitals([]);
      await refresh();
    }, "Prestataires ajoutés au réseau : créez maintenant la grille de chacun.");

  const handleDetachHospital = (hospital: NetworkHospital) => {
    if (!selectedId || !window.confirm(`Retirer « ${hospital.name} » du réseau ?`)) return;
    return run(async () => {
      await detachAdminInsurerHospital(selectedId, hospital.hospital_id);
      await refresh();
    }, "Prestataire retiré du réseau.");
  };

  /** Ouvre les grilles de ce prestataire pour cet assureur (assureur actif = assureur de la fiche). */
  const openGrids = (hospitalId: number) => {
    if (!selectedId) return;
    setWorkContext({ insurerId: selectedId });
    navigate(`/dashboard/claims/grids?hospital=${hospitalId}`);
  };

  const tabButton = (value: Tab, label: string) => (
    <button
      type="button"
      onClick={() => setTab(value)}
      className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
        tab === value ? "border-primary text-primary" : "border-transparent text-muted hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );

  const backToList = (
    <button type="button" className={ghostClass} onClick={() => { setError(null); setView({ mode: "list" }); }}>
      <ArrowLeft size={14} />
      Liste des assureurs
    </button>
  );

  return (
    <>
      <Breadcrumbs
        items={[
          { label: "Assureurs" },
          ...(view.mode === "create" ? [{ label: "Créer un assureur" }] : []),
          ...(selected ? [{ label: selected.name }] : []),
        ]}
      />

      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

      {view.mode === "list" ? (
        <section className="theme-card p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-foreground">Liste des assureurs</h1>
              <p className="mt-1 text-sm text-muted">{insurers.length} assureur(s)</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (code, nom)" aria-label="Rechercher un assureur" className={`${inputClass} sm:w-56`} />
              <button type="button" className={buttonClass} onClick={() => { setError(null); setView({ mode: "create" }); }}>
                <Plus size={15} />
                Créer assureur
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="border-b border-border bg-card-muted/50 text-left text-[11px] uppercase tracking-wide text-muted">
                <tr>
                  {["#", "Code", "Nom", "Fin de contrat", "Nbre entreprises", "Nbre prestataires", "Statut", ""].map((title) => (
                    <th key={title} className="px-4 py-3 font-semibold">{title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredInsurers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-muted">Aucun assureur.</td>
                  </tr>
                ) : null}
                {filteredInsurers.map((insurer, index) => {
                  const expired = insurer.contract_end !== null && insurer.contract_end.slice(0, 10) < new Date().toISOString().slice(0, 10);
                  return (
                    <tr key={insurer.id} className="border-b border-border last:border-0 hover:bg-card-muted/30">
                      <td className="px-4 py-3 text-muted">{index + 1}</td>
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-primary">{insurer.code}</td>
                      <td className="px-4 py-3 font-semibold text-foreground">{insurer.name}</td>
                      <td className={`px-4 py-3 ${expired ? "text-red-600" : "text-foreground"}`}>
                        {formatDate(insurer.contract_end)}
                        {expired ? <span className="ml-1 text-xs">(échu)</span> : null}
                      </td>
                      <td className="px-4 py-3 tabular-nums text-foreground">{insurer.companies_count}</td>
                      <td className="px-4 py-3 tabular-nums text-foreground">
                        {insurer.network_count}
                        {insurer.network_without_grid > 0 ? (
                          <span className="ml-1 text-xs text-amber-700">({insurer.network_without_grid} sans grille)</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge variant={insurer.status === "active" ? "success" : "danger"}>{insurer.status === "active" ? "ACTIF" : "INACTIF"}</StatusBadge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => openDetail(insurer.id)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded border border-teal/50 text-teal hover:bg-teal-soft"
                          title="Voir"
                          aria-label={`Voir ${insurer.name}`}
                        >
                          <Eye size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {view.mode === "create" ? (
        <section className="theme-card flex flex-col gap-5 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-xl font-bold text-foreground">Créer un assureur</h1>
            {backToList}
          </div>
          <p className="flex items-center gap-2 text-sm text-muted">
            <Lock size={14} />
            Le nom et le code ne pourront plus être modifiés après l'enregistrement.
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <FormField label="Nom" required>
              <input value={newInsurer.name} onChange={(e) => setNewInsurer({ ...newInsurer, name: e.target.value })} placeholder="Activa Assurances RDC" className={inputClass} />
            </FormField>
            <FormField label="Code" hint="Lettres et chiffres (ex. ACTIVA)." required>
              <input value={newInsurer.code} onChange={(e) => setNewInsurer({ ...newInsurer, code: e.target.value.toUpperCase() })} placeholder="ACTIVA" className={inputClass} />
            </FormField>
            <FormField label="Début du contrat">
              <input type="date" value={newInsurer.contract_start} onChange={(e) => setNewInsurer({ ...newInsurer, contract_start: e.target.value })} className={inputClass} />
            </FormField>
            <FormField label="Fin du contrat">
              <input type="date" value={newInsurer.contract_end} onChange={(e) => setNewInsurer({ ...newInsurer, contract_end: e.target.value })} className={inputClass} />
            </FormField>
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold text-foreground">Délai de traitement convenu</h3>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <FormField label="Délai total (jours)" required hint="De la réception de la facture à la note de paiement.">
                <input type="number" min={1} value={newInsurer.processing_days} onChange={(e) => setNewInsurer({ ...newInsurer, processing_days: e.target.value })} placeholder="30" className={inputClass} />
              </FormField>
              <FormField label="Part Admin (%)" required>
                <input type="number" min={1} value={newInsurer.share_admin} onChange={(e) => setNewInsurer({ ...newInsurer, share_admin: e.target.value })} className={inputClass} />
              </FormField>
              <FormField label="Part médecin (%)" required>
                <input type="number" min={1} value={newInsurer.share_audit} onChange={(e) => setNewInsurer({ ...newInsurer, share_audit: e.target.value })} className={inputClass} />
              </FormField>
              <FormField label="Part Sinistre (%)" required>
                <input type="number" min={1} value={newInsurer.share_claims} onChange={(e) => setNewInsurer({ ...newInsurer, share_claims: e.target.value })} className={inputClass} />
              </FormField>
            </div>
            {processingSplit(newInsurer.processing_days, newInsurer.share_admin, newInsurer.share_audit, newInsurer.share_claims) ? (
              <p className="mt-1 text-xs text-muted">
                {processingSplit(newInsurer.processing_days, newInsurer.share_admin, newInsurer.share_audit, newInsurer.share_claims)}
              </p>
            ) : null}
          </div>
          <CataloguePicker
            title="Réseau : prestataires à reprendre"
            items={allHospitalItems}
            selected={newHospitalIds}
            onChange={setNewHospitalIds}
            empty="Aucun prestataire : créez-les dans Prestataires."
          />
          <p className="text-xs text-muted">
            Ensuite : une grille tarifaire pour chaque prestataire (obligatoire), les packages de l'assureur, puis ses entreprises
            avec leurs packages autorisés.
          </p>
          <button
            type="button"
            className={`${buttonClass} self-end`}
            disabled={saving || !newInsurer.name.trim() || !newInsurer.code.trim() || !Number(newInsurer.processing_days)}
            onClick={() => void handleCreateInsurer()}
          >
            <Save size={15} />
            Enregistrer l'assureur
          </button>
        </section>
      ) : null}

      {view.mode === "detail" ? (
        <section className="theme-card p-5">
          {!selected || !profile ? (
            <p className="text-sm text-muted">Chargement…</p>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h1 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                    <Building2 size={18} />
                    {selected.name}
                    <span className="font-mono text-sm font-normal text-muted">({selected.code})</span>
                  </h1>
                  <p className="text-sm text-muted">
                    Contrat SafeCheck : {formatDate(selected.contract_start)} → {formatDate(selected.contract_end)}
                    {[selected.city, selected.phone].filter(Boolean).length ? ` · ${[selected.city, selected.phone].filter(Boolean).join(" · ")}` : ""}
                  </p>
                </div>
                {backToList}
              </div>

              <div className="flex flex-wrap gap-1 border-b border-border">
                {tabButton("profile", "Fiche assureur")}
                {tabButton("companies", `Entreprises (${companies.length})`)}
                {tabButton("network", `Réseau et grilles (${network.length})`)}
              </div>

              {tab === "profile" ? (
                <div className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <FormField label="Nom" hint="Figé après l'enregistrement.">
                      <input value={selected.name} readOnly disabled className={inputClass} />
                    </FormField>
                    <FormField label="Code" hint="Figé après l'enregistrement.">
                      <input value={selected.code} readOnly disabled className={inputClass} />
                    </FormField>
                    <FormField label="Statut">
                      <select value={profile.status} onChange={(e) => setProfile({ ...profile, status: e.target.value as ProfileForm["status"] })} className={selectClass}>
                        <option value="active">Actif</option>
                        <option value="inactive">Inactif</option>
                      </select>
                    </FormField>
                  </div>
                  {PROFILE_GROUPS.map((group) => (
                    <div key={group.title}>
                      <h3 className="mb-2 text-sm font-semibold text-foreground">{group.title}</h3>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                        {group.fields.map((field) => (
                          <FormField key={field.key} label={field.label}>
                            <input
                              type={field.type ?? "text"}
                              value={profile[field.key]}
                              placeholder={field.placeholder}
                              onChange={(e) => setProfile({ ...profile, [field.key]: e.target.value })}
                              className={inputClass}
                            />
                          </FormField>
                        ))}
                      </div>
                      {group.title === "Délai de traitement convenu" &&
                      processingSplit(profile.processing_days, profile.share_admin, profile.share_audit, profile.share_claims) ? (
                        <p className="mt-1 text-xs text-muted">
                          {processingSplit(profile.processing_days, profile.share_admin, profile.share_audit, profile.share_claims)}
                          {" "}— enregistrer applique ces délais à chaque étape (modifiables ensuite dans Délais de traitement).
                        </p>
                      ) : null}
                    </div>
                  ))}
                  <button type="button" className={`${buttonClass} self-end`} disabled={saving} onClick={() => void handleSaveProfile()}>
                    <Save size={15} />
                    Enregistrer la fiche
                  </button>
                </div>
              ) : null}

              {tab === "companies" ? (
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-muted">
                    Entreprises clientes de {selected.name}, créées en amont dans Entreprises. Chacune a ses packages autorisés,
                    parmi les packages de l'assureur ; le package de chaque agent se choisit sur la fiche de l'entreprise.
                  </p>

                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-card-muted text-left text-xs uppercase text-muted">
                        <tr>
                          <th className="px-3 py-2">Entreprise</th>
                          <th className="px-3 py-2">Packages autorisés</th>
                          <th className="px-3 py-2 text-right">Agents</th>
                          <th className="px-3 py-2 text-right">Ayants droit</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {companies.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-3 py-3 text-muted">Aucune entreprise. Ajoutez-en depuis le catalogue ci-dessous.</td>
                          </tr>
                        ) : null}
                        {companies.map((row) => (
                          <tr key={row.id} className="border-t border-border align-top">
                            <td className="px-3 py-2">
                              <span className="block font-medium text-foreground">{row.name}</span>
                              <span className="block text-xs text-muted">{[row.sector, row.city].filter(Boolean).join(" · ") || "—"}</span>
                            </td>
                            <td className="px-3 py-2">
                              {editPackages?.companyId === row.id ? (
                                <div className="flex flex-col gap-2">
                                  <PackageChecklist packages={packages} selected={editPackages.ids} onChange={(ids) => setEditPackages({ companyId: row.id, ids })} />
                                  <div className="flex gap-2">
                                    <button type="button" className={buttonClass} disabled={saving || editPackages.ids.length === 0} onClick={() => void handleSavePackages()}>
                                      <Save size={14} />
                                      Enregistrer
                                    </button>
                                    <button type="button" className={ghostClass} onClick={() => setEditPackages(null)}>
                                      <X size={14} />
                                      Annuler
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex flex-wrap gap-1">
                                  {(row.packages ?? []).length === 0 ? <span className="text-xs text-amber-700">Aucun package</span> : null}
                                  {(row.packages ?? []).map((pkg) => (
                                    <span key={pkg.id} className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">{pkg.name}</span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-foreground">{row.agents_count ?? 0}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-foreground">{row.dependants_count ?? 0}</td>
                            <td className="px-3 py-2">
                              <div className="flex justify-end gap-1">
                                <button
                                  type="button"
                                  className={rowAction}
                                  onClick={() => setEditPackages({ companyId: row.id, ids: (row.packages ?? []).map((pkg) => pkg.id) })}
                                >
                                  <Package size={13} />
                                  Packages
                                </button>
                                <Link to={`/dashboard/companies/${row.id}`} className={rowAction} title="Agents, ayants droit et leur statut">
                                  <Users size={13} />
                                  Agents
                                </Link>
                                <button
                                  type="button"
                                  aria-label={`Retirer ${row.name} de ${selected.name}`}
                                  title="Retirer de cet assureur"
                                  className="rounded-md p-1 text-muted transition-colors hover:bg-card-muted hover:text-red-600"
                                  disabled={saving}
                                  onClick={() => void handleDetachCompany(row)}
                                >
                                  <Unlink size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {pickCompanies ? (
                    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card-muted/40 p-3">
                      <CataloguePicker
                        title="Entreprises du catalogue"
                        items={otherCompanyItems}
                        selected={pickCompanies}
                        onChange={setPickCompanies}
                        empty="Aucune entreprise disponible. Créez-la d'abord dans Entreprises."
                      />
                      <div>
                        <h3 className="mb-2 text-sm font-semibold text-foreground">Packages autorisés (au moins un)</h3>
                        <PackageChecklist packages={packages} selected={pickPackages} onChange={setPickPackages} />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className={buttonClass}
                          disabled={saving || pickCompanies.length === 0 || pickPackages.length === 0}
                          onClick={() => void handleAttachCompanies()}
                        >
                          <Plus size={15} />
                          Ajouter ({pickCompanies.length})
                        </button>
                        <button type="button" className={ghostClass} onClick={() => { setPickCompanies(null); setPickPackages([]); }}>
                          <X size={14} />
                          Annuler
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className={buttonClass} onClick={() => setPickCompanies([])}>
                        <Library size={15} />
                        Ajouter une entreprise
                      </button>
                      <Link to="/dashboard/companies/create" className={ghostClass}>
                        <Plus size={14} />
                        Créer une nouvelle entreprise
                      </Link>
                    </div>
                  )}
                </div>
              ) : null}

              {tab === "network" ? (
                <div className="flex flex-col gap-4">
                  <p className="text-sm text-muted">
                    Prestataires conventionnés par {selected.name}. Chaque prestataire doit avoir une{" "}
                    <strong>grille tarifaire négociée par cet assureur</strong> : le même prestataire peut avoir une grille différente
                    chez un autre assureur. Les montants facturés sont contrôlés contre la grille en vigueur à la date des soins.
                  </p>
                  {withoutGrid > 0 ? (
                    <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      {withoutGrid} prestataire(s) sans grille : la grille est obligatoire, créez-la avec « Créer la grille ».
                    </p>
                  ) : null}

                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-card-muted text-left text-xs uppercase text-muted">
                        <tr>
                          <th className="px-3 py-2">Prestataire</th>
                          <th className="px-3 py-2">Grille de {selected.code}</th>
                          <th className="px-3 py-2">Validité</th>
                          <th className="px-3 py-2 text-right">Actes</th>
                          <th className="px-3 py-2">Autres assureurs</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {network.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-3 py-3 text-muted">Aucun prestataire dans le réseau.</td>
                          </tr>
                        ) : null}
                        {network.map((hospital) => {
                          const grid = hospital.grid_in_force;
                          const grids = hospital.grids ?? [];
                          const others = hospital.other_insurers_with_grid ?? 0;
                          return (
                            <tr key={hospital.hospital_id} className="border-t border-border align-top">
                              <td className="px-3 py-2">
                                <span className="block font-medium text-foreground">{hospital.name}</span>
                                <span className="block text-xs text-muted">{[hospital.type, hospital.city].filter(Boolean).join(" · ") || "—"}</span>
                              </td>
                              <td className="px-3 py-2">
                                {grid ? (
                                  <>
                                    <span className="block font-mono text-xs font-semibold text-primary">{grid.code}</span>
                                    <span className="block text-xs text-muted">{grid.name} · {grid.currency}</span>
                                  </>
                                ) : grids.length > 0 ? (
                                  <span className="text-xs text-amber-700">Aucune grille en vigueur</span>
                                ) : (
                                  <StatusBadge variant="danger">GRILLE OBLIGATOIRE</StatusBadge>
                                )}
                                {grids.length > 1 ? <span className="block text-[11px] text-muted">{grids.length} grilles au total</span> : null}
                              </td>
                              <td className="px-3 py-2 text-xs text-foreground">{grid ? `${formatDate(grid.valid_from)} → ${grid.valid_to ? formatDate(grid.valid_to) : "sans fin"}` : "—"}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-foreground">{grid ? grid.acts_count : "—"}</td>
                              <td className="px-3 py-2 text-xs text-muted">{others > 0 ? `${others} autre(s) assureur(s), avec leur propre grille` : "—"}</td>
                              <td className="px-3 py-2">
                                <div className="flex justify-end gap-1">
                                  <button type="button" className={rowAction} onClick={() => openGrids(hospital.hospital_id)}>
                                    <FileSpreadsheet size={13} />
                                    {grids.length > 0 ? "Gérer la grille" : "Créer la grille"}
                                  </button>
                                  <button
                                    type="button"
                                    aria-label={`Retirer ${hospital.name} du réseau`}
                                    className="rounded-md p-1 text-muted transition-colors hover:bg-card-muted hover:text-red-600"
                                    disabled={saving}
                                    onClick={() => void handleDetachHospital(hospital)}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <CataloguePicker
                    title="Ajouter des prestataires du catalogue"
                    items={otherHospitalItems}
                    selected={pickHospitals}
                    onChange={setPickHospitals}
                    empty="Tous les prestataires du catalogue sont déjà dans ce réseau. Les nouveaux se créent dans Prestataires."
                  />
                  <button type="button" className={`${buttonClass} self-start`} disabled={saving || pickHospitals.length === 0} onClick={() => void handleAttachHospitals()}>
                    <Plus size={15} />
                    Ajouter au réseau ({pickHospitals.length})
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </section>
      ) : null}
    </>
  );
}
